import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import {
  ComposedChart,
  Line,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { format, parseISO } from 'date-fns';
import { cn } from '@/lib/utils';
import type { LeverageSeriesPoint } from '@/lib/hyperliquid';

interface Props {
  data: LeverageSeriesPoint[];
  className?: string;
  loading?: boolean;
}

export function LeverageOverlayChart({ data, className, loading = false }: Props) {
  if (loading) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle className="text-base">Account Value & Leverage</CardTitle>
          <CardDescription>Loading...</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-72 bg-muted/30 rounded animate-pulse" />
        </CardContent>
      </Card>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle className="text-base">Account Value & Leverage</CardTitle>
          <CardDescription>Track how your leverage changes with account value</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-72 flex items-center justify-center text-muted-foreground text-sm">
            No leverage data available
          </div>
        </CardContent>
      </Card>
    );
  }

  // Sample data if too many points (keep max 100 for performance)
  let chartData = data;
  if (data.length > 100) {
    const step = Math.ceil(data.length / 100);
    chartData = data.filter((_, i) => i % step === 0);
  }

  const formattedData = chartData.map((point) => ({
    time: format(parseISO(point.time), 'MMM dd'),
    fullTime: format(parseISO(point.time), 'MMM dd, HH:mm'),
    accountValue: Number(point.accountValue.toFixed(2)),
    leverage: Number(point.effectiveLeverage.toFixed(2)),
  }));

  const formatCurrency = (value: number) => {
    if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `$${(value / 1000).toFixed(1)}k`;
    return `$${value.toFixed(0)}`;
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const point = payload[0]?.payload;
      return (
        <div className="bg-popover/95 backdrop-blur-sm border border-border/50 rounded-lg p-3 shadow-xl">
          <p className="text-xs font-medium text-foreground mb-2">{point?.fullTime || label}</p>
          <div className="space-y-1">
            <div className="flex items-center justify-between gap-4">
              <span className="text-xs text-muted-foreground">Account Value</span>
              <span className="text-sm font-mono font-semibold text-blue-500">
                {formatCurrency(point?.accountValue || 0)}
              </span>
            </div>
            <div className="flex items-center justify-between gap-4">
              <span className="text-xs text-muted-foreground">Leverage</span>
              <span className={cn(
                "text-sm font-mono font-semibold",
                point?.leverage < 3 ? "text-emerald-500" :
                point?.leverage < 8 ? "text-amber-500" : "text-rose-500"
              )}>
                {point?.leverage?.toFixed(2)}x
              </span>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  // Calculate Y-axis domains
  const accountValues = formattedData.map(d => d.accountValue);
  const leverageValues = formattedData.map(d => d.leverage);
  
  const minAV = Math.min(...accountValues) * 0.95;
  const maxAV = Math.max(...accountValues) * 1.05;
  const maxLev = Math.max(...leverageValues, 1) * 1.2;

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="text-base">Account Value & Leverage Over Time</CardTitle>
        <CardDescription>Track correlation between account value and leverage usage</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={formattedData}>
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="hsl(var(--border))"
                strokeOpacity={0.3}
                vertical={false}
              />
              <XAxis
                dataKey="time"
                axisLine={false}
                tickLine={false}
                tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 10 }}
                tickMargin={10}
                interval="preserveStartEnd"
              />
              <YAxis
                yAxisId="left"
                axisLine={false}
                tickLine={false}
                tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 10 }}
                tickFormatter={formatCurrency}
                domain={[minAV, maxAV]}
                orientation="left"
              />
              <YAxis
                yAxisId="right"
                orientation="right"
                axisLine={false}
                tickLine={false}
                tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 10 }}
                tickFormatter={(v) => `${v.toFixed(1)}x`}
                domain={[0, maxLev]}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend
                verticalAlign="top"
                height={36}
                formatter={(value) => (
                  <span className="text-xs text-muted-foreground">{value}</span>
                )}
              />
              <Line
                yAxisId="left"
                type="monotone"
                dataKey="accountValue"
                stroke="hsl(217, 91%, 60%)"
                strokeWidth={2}
                name="Account Value"
                dot={false}
                activeDot={{ r: 4, fill: "hsl(217, 91%, 60%)" }}
              />
              <Bar
                yAxisId="right"
                dataKey="leverage"
                fill="hsl(0, 84%, 60%)"
                fillOpacity={0.3}
                name="Leverage"
                radius={[2, 2, 0, 0]}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}
